import {Link, useNavigate} from 'react-router-dom';
import {Suspense, useEffect, useState} from "react";
import {toast, ToastContainer} from "react-toastify";
import Header from "../Header.jsx";
import Button from "../component-assets/Button.jsx";
import ApiRequester from "../ApiRequester.jsx";
import {
    formCheck,
    formCheckWithElement,
    formClear,
    formFail,
    formSuccess,
    areaLearninBehaviorToggle,
    handleSubmitActions,
    hideModal,
    showModal,
    sortFunc,
    swapModal,
    toggleCollapse,
    iAmHigherAuthority,
    setAreaFilters,
    formatDateIso,
    getToday,
    HHMMtoLocalTime,
    convertSectoHHMM,
    isBannerPresent,
    versionSort,
    arrayComparison, paginateData
} from "../../libs.jsx";
import {Chart, registerables} from "chart.js";
import TableBody from "../component-assets/TableBody.jsx";
import annotationPlugin from "chartjs-plugin-annotation";
import TableHeaders from "../component-assets/TableHeaders.jsx";
import Navigation from "../Navigation.jsx";
import HourlyLineAndBarChart from "../charts/HourlyLineAndBarChart.jsx";
import LineTrendingChart from "../charts/LineTrendingChart.jsx";
import ProfileSkeleton from "../component-assets/skeletons/ProfileSkeleton.jsx";
import {Pagination} from "react-bootstrap";
import PaginationItems from "../component-assets/PaginationItems.jsx";
import { getSession, getActiveVendorIdList } from "../../services/session";

const AreaProfile = (props) => {
    let dData, bData, startDate, endDate, eventHeaderData, noteHeaderData, logHeaderData;

    const navigate = useNavigate();

    dData = getSession();

    Chart.register(...registerables);
    Chart.register(annotationPlugin);

    eventHeaderData = [
        {label: "Event Start", key: "event_datetime_local", align: "center", sorts: false,
            link: {enabled: true, linkto: "/profiles/event", linkkey: "event_id", type: "datetime"}},
        {label: "Response (Min)", key: "response_time", align: "center", sorts: false},
        {label: "Device", key: ["description", "manufacture_uuid"], align: "center", sorts: false,
            link: {enabled: true, type: "stack", linkto: "/profiles/inovonics", linkkey: "device_id"}},
        {label: "Resident", key: "resident_name", align: "left", sorts: false,
            link: {enabled: true, type: "standard", linkto: "/profiles/resident", linkkey: "resident_id"}},
        {label: "Reason", key: "reason_code_description", align: "center", type: "indicator", sorts: false,
            indicator: {enabled: dData?.is_reason_editable, type: "length", key: "reason_edits",
                title: "This value has been edited"}}
    ];

    noteHeaderData = [
        {label: "Timestamp", key: "create_time", align: "center", type: "datetime-utc", sorts: false},
        {label: "Created By", key: "display_name", align: "center", sorts: false},
        {label: "Note", key: "note", align: "left", sorts: false},
    ];

    logHeaderData = [
        {label: "Timestamp", key: "create_time", align: "center", type: "datetime-utc", sorts: false},
        {label: "Log Event", key: "area_log_type_name", align: "center", sorts: false, filtered: true},
        {label: "Modified By", key: "display_name", align: "center", sorts: false, filtered: true}
    ];

    // Permission Variables
    const [editRights, setEditRights] = useState(iAmHigherAuthority(20, dData.role_weight));
    const [devEditRights, setDevEditRights] = useState(iAmHigherAuthority(10, dData.role_weight))
    const [deleteRights, setDeleteRights] = useState(iAmHigherAuthority(20, dData.role_weight));
    // Main Page Variables
    const [areaId, setAreaId] = useState(props?.id);
    const [count, setCount] = useState(0);
    const [repCount, setRepCount] = useState(0);
    const [campusId, setCampusId] = useState(dData.acnt);
    const [tabItem, setTabItem] = useState("init");
    const [storedIndex, setStoredIndex] = useState(0);
    const [prenoteData, setPrenoteData] = useState([]);
    const [prenoteOptions, setPrenoteOptions] = useState([]);
    const [read, setRead] = useState(true);
    // Load Variables
    const [basicDataLoaded, setBasicDataLoaded] = useState(false); // Goes into Basic
    const [areaTypeLoaded, setAreaTypeLoaded] = useState(false); // Goes into Basic
    const [basicLoaded, setBasicLoaded] = useState(false); // Handles Basic Tab Display
    const [buildingDataLoaded, setBuildingDataLoaded] = useState(false);
    const [areaDevicesLoaded, setAreaDevicesLoaded] = useState(false); // Goes into Device
    const [deviceDataLoaded, setDeviceDataLoaded] = useState(false); // Goes into Device
    const [deviceCatLoaded, setDeviceCatLoaded] = useState(false); // Goes into Device
    const [deviceTypeLoaded, setDeviceTypeLoaded] = useState(false); // Goes into Device
    const [devicesLoaded, setDevicesLoaded] = useState(false); // Handles Device Tab Display
    const [hourDataLoaded, setHourDataLoaded] = useState(false); // Goes into Report
    const [weekDataLoaded, setWeekDataLoaded] = useState(false); // Goes into Report
    const [eventDataLoaded, setEventDataLoaded] = useState(false); // Goes into Report
    const [reasonDataLoaded, setReasonDataLoaded] = useState(false); // Goes into Report
    const [reportLoaded, setReportLoaded] = useState(false); // Handles Report Tab Display
    const [careDataLoaded, setCareDataLoaded] = useState(false); // Goes into Wellness
    const [roundsDataLoaded, setRoundsDataLoaded] = useState(false); // Goes into Wellness
    const [wellnessLoaded, setWellnessLoaded] = useState(false); // Handles Wellness Tab Display
    const [logDataLoaded, setLogDataLoaded] = useState(false); // Goes into Logs
    const [logsLoaded, setLogsLoaded] = useState(false); // Handles Logs Tab Display
    const [pageLoaded, setPageLoaded] = useState(false);
    // LineTrendingVariables
    const [lineData, setLineData] = useState([]);
    const [lineColors, setLineColors] = useState([]);
    const [lineLabels, setLineLabels] = useState([]);
    const [lineAxisLabels, setLineAxisLabels] = useState([]);
    // Basic Information Variables
    const [basicData, setBasicData] = useState([]);
    const [buildingData, setBuildingData] = useState([]);
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [areaName, setAreaName] = useState("");
    const [maxOccupancy, setMaxOccupancy] = useState("");
    const [residents, setResidents] = useState("");
    const [areaType, setAreaType] = useState("");
    const [areaTypeId, setAreaTypeId] = useState("");
    const [areaTypeData, setAreaTypeData] = useState([]);
    const [areaTypeOptions, setAreaTypeOptions] = useState([]);
    const [areaGroups, setAreaGroups] = useState([]);
    const [buildingId, setBuildingId] = useState("");
    const [building, setBuilding] = useState("");
    // Device Information Variables
    const [areaDeviceData, setAreaDeviceData] = useState([]);
    const [deviceData, setDeviceData] = useState([])
    const [devices, setDevices] = useState([])
    const [deviceToRemove, setDeviceToRemove] = useState("");
    const [assignedArea, setAssignedArea] = useState("");
    // Device Learn in Variables
    const [listening, setListening] = useState(false); // Flag indicating listening started, stopped
    const [listenEnabled, setListenEnabled] = useState(false); // Flag if listen feature ready or not
    const [listenCount, setListenCount] = useState(60);  // Counter for fetching and toggle listen off
    const [listenData, setListenData] = useState([]);  // Data response of devices heard
    const [listenList, setListenList] = useState([]); // Drop down options for heard devices serial numbers
    const [deviceCatData, setDeviceCatData] = useState([]);  // Device category data used for drop down in learn in
    const [deviceCatOptions, setDeviceCatOptions] = useState([]);  // Device category drop down options for learn in
    const [deviceTypeData, setDeviceTypeData] = useState([]); // Data used for device type drop down in learn in
    const [deviceTypeOptions, setDeviceTypeOptions] = useState([]); // Device type drop down options for learn in
    const [deviceCatId, setDeviceCatId] = useState(0);  // Device Category set in learn in
    const [deviceTypeId, setDeviceTypeId] = useState([]);  // Device Type set in learn in
    const [modelId, setModelId] = useState(0);  // Model selected in list
    const [modelOptions, setModelOptions] = useState([]);
    const [msgClass, setMsgClass] = useState(0);
    const [productCode, setProductCode] = useState(0);
    const [marketId, setMarketId] = useState(0);
    const [allProductCodes, setAllProductCodes] = useState([]);
    const [showDeviceList, setShowDeviceList] = useState(false); // Toggles showing drop down serial or not and required validation
    const [listenDevice, setListenDevice] = useState("");
    const [deviceTypeName, setDeviceTypeName] = useState("");
    const [listenPCode, setListenPCode] = useState(0);
    const [useOptions, setUseOptions] = useState([]);
    const [useId, setUseId] = useState(0);
    const [showAlarmBehaviors, setShowAlarmBehaviors] = useState(false);
    const [deviceImg, setDeviceImg] = useState("");
    // Wellness Variables
    const [areaWellData, setAreaWellData] = useState([]);
    const [careData, setCareData] = useState([]);
    const [roundsData, setRoundsData] = useState([]);
    const [careflow, setCareflow] = useState([]);
    const [rounds, setRounds] = useState([]);
    // Reporting Variables
    const [weekData, setWeekData] = useState({});
    const [hourData, setHourData] = useState({});
    const [reasonData, setReasonData] = useState({});
    const [reasonRows, setReasonRows] = useState([]);
    const [eventData, setEventData] = useState([]);
    const [eventTableRows, setEventTableRows] = useState([]);
    const [noteTableRows, setNoteTableRows] = useState([]);
    const [noteTableLength, setNoteTableLength] = useState(0);
    // Logging Variables
    const [logOriginData, setLogOriginData] = useState([]);
    const [logActiveData, setLogActiveData] = useState([]);
    const [logTableRows, setLogTableRows] = useState([]);
    const [logHeaderFilters, setLogHeaderFilters] = useState([]);
    const [logSorter, setLogSorter] = useState({
        sortColumn: "create_time",
        sortOrder: "desc"
    });
    const [logPPRules, setLogPRules] = useState({
        start: 0,
        end: 1,
        beginDot: false,
        endDot: false,
        pageMin: 1,
        pageMax: 1,
        max: 1
    });
    const [logPage, setLogPage] = useState(1);
    const [perPage, setPerPage] = useState(dData.perPage);
    const [storePage, setStorePage] = useState(1);
    const [tmpPerPage, setTmpPerPage] = useState(dData.perPage);

    startDate = formatDateIso(getToday(-28));
    endDate = formatDateIso(getToday());

    useEffect(() => {
        let interval = null;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        if (count <= 0){
            ApiRequester({reqEndpoint: "myfetch", payload: {area_id: areaId, campus_id: campusId},
                          procedure: "getareaprofile", reqType: "stored", "read-only": read}).then(data => {
                setBasicData(data);
                setBasicDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch",
                          payload: {campus_id: campusId, care_types: "1,5", care_event_types: "1"},
                          procedure: "getcareflowwindows", reqType: "stored", "read-only": true}).then(data => {
                setCareData(data);
                setCareDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch",
                          payload: {campus_id: campusId, window_types: "2"},
                          procedure: "getwellnesswindows", reqType: "stored", "read-only": true}).then(data => {
                setRoundsData(data);
                setRoundsDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", payload: {area_id: areaId, campus_id: campusId},
                          procedure: "getareadevices", reqType: "stored", "read-only": true}).then(data => {
                setAreaDeviceData(data);
                setAreaDevicesLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", payload: {device_filter: "",
                                device_category_filter: "1,2,3,6,7,8,9,10,11,12,15,16,17", campus_id: campusId},
                                procedure: "sp_dev_getInovonicsDevices", reqType: "stored", "read-only": true}).then(data => {
                setDeviceData(data);
                setDeviceDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt, Tbl: "att_area_type",
                                procedure: "fetchall", reqType: "direct", "read-only": true}).then(data => {
                setAreaTypeData(data);
                setAreaTypeLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt,
                                procedure: "getareadevicecategories", reqType: "stored", "read-only": true}).then(data => {
                setDeviceCatData(data);
                setDeviceCatLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt,
                                procedure: "getinovonicsdevicetypes", reqType: "stored", "read-only": true}).then(data => {
                setDeviceTypeData(data);
                setDeviceTypeLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "area_id", pVal: areaId,
                                procedure: "getarealogs", reqType: "stored", "read-only": read}).then(data => {
                setLogOriginData(data);
                setLogDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt, Tbl: "core_building",
                                procedure: "fetchbyparameter", reqType: "direct"}).then(data => {
                setBuildingData(data);
                setBuildingDataLoaded(true);
            });
            setCount(() => 600);
        }
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        let interval = null;
        interval = setInterval(() => {
             setRepCount(repCount => repCount - 1);
        }, 1000);
        if (repCount <= 0){
            {dData?.event_notes_active && dData.event_notes_preform &&
                ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt, Tbl: "core_preform_event_notes",
                                procedure: "fetchbyparameter", reqType: "direct", "read-only": read}).then(data => {
                    setPrenoteData(data);
                });
            }
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardhourchart",
                                campus_id: campusId, area_filter: areaId.toString()}).then(data => {
                setHourData(data);
                setHourDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardweekchart",
                                campus_id: campusId, area_filter: areaId.toString()}).then(data => {
                setWeekData(data);
                setWeekDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "eventReasonSummary",
                        campus_id: campusId, start_date: startDate, end_date: endDate,
                        device_category_filter: "", area_type_filter: "", reason_filter: "", user_filter: "",
                        event_type_filter: "", resident_filter: "", area_filter: areaId.toString()}).then(data => {
                setReasonData(() => data ? data : []);
                setReasonDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", payload: {campus_id: campusId, area_id: areaId.toString()},
                                procedure: "getlimitedevents", reqType: "stored", "read-only": true}).then(data => {
                setEventData(data);
                setEventDataLoaded(true);
            });
            setRepCount(repCount => 1200);
        }
        return () => clearInterval(interval);
    }, [repCount]);

    useEffect(() => {
        if (![basicLoaded, devicesLoaded, reportLoaded, wellnessLoaded, logsLoaded].includes(false)){
            setPageLoaded(true);
        }
    }, [basicLoaded, devicesLoaded, reportLoaded, wellnessLoaded, logsLoaded]);

    useEffect(() => {
        if ([basicDataLoaded, areaTypeLoaded, buildingDataLoaded].every((item) => item === true)){
            setBasicLoaded(true);
            if (tabItem === "init"){
                setTabItem("basic");
            }
        }
    }, [basicDataLoaded, areaTypeLoaded, buildingDataLoaded]);

    useEffect(() => {
        if ([hourDataLoaded, weekDataLoaded, eventDataLoaded, reasonDataLoaded].every((item) => item === true)){
            setReportLoaded(true);
        }
    }, [hourDataLoaded, weekDataLoaded, eventDataLoaded, reasonDataLoaded]);

    useEffect(() => {
        if ([areaDevicesLoaded, deviceDataLoaded, deviceCatLoaded, deviceTypeLoaded].every((item) => item === true)){
            setDevicesLoaded(true);
        }
    }, [areaDevicesLoaded, deviceDataLoaded, deviceCatLoaded, deviceTypeLoaded]);

    useEffect(() => {
        if ([roundsDataLoaded, careDataLoaded, basicDataLoaded].every((item) => item === true)){
            setWellnessLoaded(true);
        }
    }, [roundsDataLoaded, careDataLoaded, basicDataLoaded]);

    useEffect(() => {
        if ([logDataLoaded].every((item) => item === true)){
            setLogsLoaded(true);
        }
    }, [logDataLoaded]);

    useEffect(() => {
        let resItems = []
        if (basicData.length > 0){
            setAreaName(() => basicData[0]?.area_name ? basicData[0].area_name : "");
            setMaxOccupancy(() => basicData[0]?.max_occupancy ? basicData[0].max_occupancy : "");
            setAreaType(() => basicData[0]?.area_type_description ? basicData[0].area_type_description : "");
            setAreaTypeId(() => basicData[0]?.area_type_id ? basicData[0].area_type_id : "");
            setAreaGroups(() => basicData[0]?.groups ? basicData[0].groups : []);
            setBuildingId(() => basicData[0]?.building_id ? basicData[0].building_id : "");
            setBuilding(() => basicData[0]?.building_name ? basicData[0].building_name : "")
            for (let i=0; i < basicData[0]?.residents?.length; i++){
                if (i > 0){
                    resItems.push(<br key={`break${i}`}/>);
                    resItems.push(<Link key={`resident${i}`} to={`/profiles/resident/${basicData[0].residents[i].resident_id}`}>{basicData[0].residents[i].resident_name}</Link>);
                }else{
                    resItems.push(<Link key={`resident${i}`} to={`/profiles/resident/${basicData[0].residents[i].resident_id}`}>{basicData[0].residents[i].resident_name}</Link>);
                }
            }
            setResidents(() => resItems);
        }
    }, [basicData]);

    useEffect(() => {
        let tmpAflows = [], tmpDflows = [], allFlows;
        if (careData && careData?.length > 0){
            for (let i=0; i < careData?.length; i++){
                if (careData[i].escalation_type_id === 1){
                    tmpDflows.push(<>
                        <div key={`cfd-1-${i}`} className={"d-inline-block"} style={{minWidth: "175px", maxWidth: "175px", textOverflow: "ellipsis"}}>
                            {careData[i].careflow_description}
                        </div>
                        <div key={`cft-1-${i}`} className={"d-inline-block align-top ms-1"}>
                            {HHMMtoLocalTime(convertSectoHHMM(careData[i].enable_start))} - {HHMMtoLocalTime(convertSectoHHMM(careData[i].enable_end))} (Default)
                        </div>
                    </>);
                }else if (careData[i].escalation_type_id === 5){
                    for (let i3=0; i3 < areaGroups?.length; i3++){
                        if (careData[i]?.references.split(",").includes(areaGroups[i3]?.area_group_id?.toString())){
                            tmpAflows.push(<>
                                <div key={`cfd-5-${i}`} className={"d-inline-block"} style={{minWidth: "175px", maxWidth: "175px", textOverflow: "ellipsis"}}>
                                    {careData[i].careflow_description}
                                </div>
                                <div key={`cft-5-${i}`} className={"d-inline-block align-top ms-1"}>
                                    {HHMMtoLocalTime(convertSectoHHMM(careData[i].enable_start))} - {HHMMtoLocalTime(convertSectoHHMM(careData[i].enable_end))} (Area)
                                </div>
                            </>);
                        }
                    }
                }
            }
            allFlows = tmpAflows.concat(tmpDflows);
            setCareflow(allFlows.map((item, i) => {return <div key={`cf-item-${i}`}>{item}<br/></div>}));
        }
    }, [careData, areaGroups]);

    useEffect(() => {
        let tmpWindows = [];
        if (roundsData && roundsData?.length > 0){
            for (let i=0; i < roundsData?.length; i++){
                for (let i3=0; i3 < areaGroups?.length; i3++){
                    if (roundsData[i]?.area_group_id_list.split(",").includes(areaGroups[i3]?.area_group_id?.toString())){
                        tmpWindows.push(<>
                            <div key={`crd-${i}`} className={"d-inline-block"} style={{minWidth: "175px", maxWidth: "175px", textOverflow: "ellipsis"}}>
                                {roundsData[i].description}
                            </div>
                            <div key={`crt-${i}`} className={"d-inline-block align-top ms-1"}>
                                {HHMMtoLocalTime(convertSectoHHMM(roundsData[i].window_start))} - {HHMMtoLocalTime(convertSectoHHMM(roundsData[i].window_end))}
                            </div>
                        </>);
                    }
                }
            }
            setRounds(tmpWindows.map((item, i) => {return <div key={`cr-item-${i}`}>{item}<br/></div>}));
        }
    }, [roundsData, areaGroups]);

    useEffect(() => {
        let tmp = [];
        if (prenoteData.length > 0){
            for (let i=0; i < prenoteData.length; i++){
                tmp.push(
                    <option key={`prenote-option-${i}`} value={prenoteData[i]?.preform_note}>{prenoteData[i]?.preform_note}</option>
                );
            }
        }
        if (dData?.event_notes_freeform){
            tmp.push(
                <option key={`prenote-option-00`} value={"0"}>Other (freeform note)</option>
            );
        }
        setPrenoteOptions(() => tmp);
    }, [prenoteData]);

    useEffect(() => {
        let tmp = [];
        if (buildingData.length > 0){
            for (let i=0; i < buildingData.length; i++){
                tmp.push(
                    <option key={`buildingData-option-${i}`} value={buildingData[i]?.building_id}
                            title={`${buildingData[i].street_address} \n${buildingData[i].city} ${buildingData[i].state} ${buildingData[i].zipcode}`}>
                        {buildingData[i]?.building_name}
                    </option>
                );
            }
        }
        setBuildingOptions(() => tmp);
    }, [buildingData]);

    useEffect(() => {
        let tmp = [];
        for (let i = 0; i < areaDeviceData?.length; i++){
            tmp.push(
                <li key={`device-name-${i}`} className={"mt-2"}>
                    {devEditRights ?
                    <i className={"fe fe-trash-2 red-hover fs-1 me-3 align-middle"} title={"Detach device"}
                       onClick={() => {
                           setDeviceToRemove(() => areaDeviceData[i]?.device_id);
                           showModal("deviceconfirmmodal")
                       }}/> : <></>}
                    <Link to={`/profiles/inovonics/${areaDeviceData[i].device_id}`}>
                        <span>{areaDeviceData[i].device_name} </span><span>{areaDeviceData[i].descriptions}</span><span> ({areaDeviceData[i].manufacture_uuid})</span>
                    </Link>
                </li>
            );
        }
        setDevices(() => tmp);
    }, [areaDeviceData, devEditRights]);

    useEffect(() => {
        // Performs realtime search of data as location types.
        let tmpData, input, pRules;

        tmpData = logOriginData.filter(item => {
            return Object.keys(logHeaderFilters).every(key => {
                if (!logHeaderFilters[key] || logHeaderFilters[key].length === 0) return true;
                return logHeaderFilters[key].includes(String(item[key]));
            });
        });

        pRules = paginateData(tmpData, logPage, perPage);
        if (input && logPage > pRules.pageMax){
            setLogPage(pRules.pageMax);
        }else if (!input){
            setLogPage(storePage);
        }
        setLogPRules(pRules);
        setLogActiveData(tmpData);
    }, [logOriginData, logHeaderFilters]);

    useEffect(() => {
        if (dData?.event_notes_active){
            setEventTableRows(<TableBody start={0} end={eventData.length} tableData={eventData} checkbox={false}
                                         writeAccess={true} unk={"a-"} modalType={"add"} modalLabel={"Add Note"}
                                         actionAppearance={() => {}} parentComponent={"Resident"}
                                         tableHeaders={eventHeaderData} modal={true} editModal={addNoteModal}
                                         dData={dData} checkboxlist={[]} editModalBadge={"notes"}
                                         setCheckboxId={() => {}} checkidkey={""} />);
        }else{
            setEventTableRows(<TableBody start={0} end={eventData.length} tableData={eventData} checkbox={false}
                                         writeAccess={true} unk={"a-"} modalType={"add"} modalLabel={"Add Note"}
                                         actionAppearance={() => {}} parentComponent={"Resident"}
                                         tableHeaders={eventHeaderData} modal={false} editModal={() => {}}
                                         dData={dData} checkboxlist={[]} editModalBadge={"notes"}
                                         setCheckboxId={() => {}} checkidkey={""} />);
        }
    }, [eventData]);

    useEffect(() => {
        let pRules = paginateData(logActiveData, logPage, perPage);
        setLogPRules(pRules);
        if (logActiveData.length > 0) {
            logActiveData.sort((a, b) => sortFunc(a, b, logSorter.sortColumn, logSorter.sortOrder));
        }
        setLogTableRows(<TableBody start={pRules.start} end={pRules.end} tableData={logActiveData} checkbox={false}
                                writeAccess={false} unk={"l-"} addlEditKey={""} addlEditCheck={false}
                                actionAppearance={() => {}}
                                tableHeaders={logHeaderData} editModal={() => {}} parentComponent={"Resident"} dData={dData}
                                modal={false} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)}
                                checkidkey={""} />);
    }, [logActiveData, logSorter, logPage, perPage]);

    useEffect(() => {
        if (deviceCatData.length > 0){
            let tmpOptions = [];
            for (let i=0; i < deviceCatData.length; i++){
                tmpOptions.push(
                    <option key={`device-category-option-${deviceCatData[i].device_category_id}`}
                            value={deviceCatData[i].device_category_id}>{deviceCatData[i].device_category_short_name}
                    </option>
                );
            }
            setDeviceCatOptions(() => tmpOptions);
        }
    }, [deviceCatData]);

    useEffect(() => {
        let i, tmp = [];
        deviceTypeData.sort((a, b) => sortFunc(a, b, "device_type_name", "asc"));
        for (i = 0; i < deviceTypeData.length; i++){
            if ((deviceTypeData[i]?.device_category_id.toString() === deviceCatId.toString()) &&
                (iAmHigherAuthority(80, dData.role_weight) || !deviceTypeData[i]?.is_third_party)){
                tmp.push(
                    <option key={`device-use-option-${deviceTypeData[i]?.inovonics_type_id}`}
                                 value={deviceTypeData[i]?.inovonics_type_id}>{deviceTypeData[i]?.device_type_name}
                    </option>
                );
            }
        }
        setDeviceTypeOptions(() => tmp);
    }, [deviceTypeData, deviceCatId]);

    useEffect(() => {
        let tmp = [], models = [], tmp2 = [], uses = [], tmp3 = [], imgstr = "";
        for (let i=0; i < deviceTypeData.length; i++){
            if (deviceTypeData[i]?.inovonics_type_id === deviceTypeId){
                models = deviceTypeData[i]?.models ? deviceTypeData[i].models : [];
                uses = deviceTypeData[i]?.uses ? deviceTypeData[i].uses : [];
                imgstr = deviceTypeData[i]?.image_url ? deviceTypeData[i].image_url : "";
                break
            }
        }
        for (let i2=0; i2 < models.length; i2++){
            if (i2 === 0){
                setModelId(models[i2].model_id);
                setMsgClass(models[i2].message_class);
                setProductCode(models[i2].product_type_id);
                setMarketId(models[i2].market_id);
            }
            tmp2.push(models[i2].product_type_id);
            tmp.push(
                <option key={`device-model-option-${i2}`}
                        value={models[i2]?.model_id}>{models[i2]?.model_name}
                </option>
            );
        }
        for (let i3=0; i3 < uses.length; i3++){
            if (i3 === 0){
                setUseId(uses[i3].inovonics_device_use_id);
                setShowAlarmBehaviors(uses[i3].option_number !== 0);
            }
            if (uses[i3].option_number !== 0){
                tmp3.push(
                    <div className={"form-check form-switch mt-1"} id={`use-option-${uses[i3].option_number}-container`}>
                        <input className={"form-check-input"} id={`use-option-${uses[i3].option_number}`} type={"radio"}
                               name={"alarmBehavior"} value={uses[i3].inovonics_device_use_id} defaultChecked={i3 === 0}
                               onChange={(e) => {setUseId(parseInt(e.target.value))}} />
                        <label className={"form-check-label me-4 mt-1 text-dark"}
                               htmlFor={`use-option-${uses[i3].option_number}`} id={`use-option-label-${uses[i3].option_number}`}>
                            {uses[i3].option_text}
                        </label>
                    </div>
                );
            }
        }
        setAllProductCodes(tmp2);
        setModelOptions(() => tmp);
        setUseOptions(() => tmp3);
        setDeviceImg(imgstr);
    }, [deviceTypeData, deviceTypeId]);

    useEffect(() => {
        let models = [];
        for (let i=0; i < deviceTypeData.length; i++){
            if (deviceTypeData[i]?.inovonics_type_id === deviceTypeId){
                models = deviceTypeData[i]?.models ? deviceTypeData[i].models : [];
                break
            }
        }
        for (let i2=0; i2 < models.length; i2++){
            if (models[i2].model_id === modelId){
                setMsgClass(models[i2].message_class);
                setProductCode(models[i2].product_type_id);
                setMarketId(models[i2].market_id);
            }
        }
        setListenEnabled(modelId !== 0);
    }, [modelId]);

    useEffect(() => {
        if (areaTypeData.length > 0){
            let tmpOptions = [];
            for (let i=0; i < areaTypeData.length; i++){
                tmpOptions.push(
                    <option key={`area-type-option-${areaTypeData[i].area_type_id}`}
                            value={areaTypeData[i].area_type_id}>
                        {areaTypeData[i].area_type_description}
                    </option>
                );
            }
            setAreaTypeOptions(() => tmpOptions);
            setAreaTypeLoaded(true);
        }
    }, [areaTypeData]);

    useEffect(() => {
        let tmp = [];
        let reasons = reasonData?.reasons ? reasonData.reasons : [];
        for (let i=0; i < reasons?.length; i++){
            tmp.push(
                <tr key={`summaryRow-reasons-${i}`}>
                    <td className={"ps-1"}>{reasons[i].reason_name}</td>
                    <td className={"ps-1 text-center"}>{reasons[i].count}</td>
                    <td className={"ps-1 text-center"}>{reasons[i].percent}</td>
                    <td className={"ps-1 text-center"}>{reasons[i]?.avg_response_time}</td>
                </tr>
            );
        }
        setReasonRows(() => tmp);
    }, [reasonData]);

    useEffect(() => {
        formCheck("pendantForm", "pendantSubmit");
    }, [listening, listenDevice]);

    useEffect(() => {
        let interval;
        if (checkListenValid()){
            interval = setInterval(() => {
                setListenCount(listenCount  => listenCount  + 1);
            }, 1000);
            if (listenCount < 60){
                if (listenCount % 4 === 0){
                    ApiRequester({reqEndpoint: "getitems", Table: "LearnIn", PKey: "ClientId",
                        PVal: dData.sessionId, CampusId: campusId}).then(data => {
                            setListenData(data);
                    });
                }
            }
            if (listenCount === 60){
                setListening(false);
                toast.info("Capture timeout, restart capture if device not in list.");
            }
        }
        return () => clearInterval(interval);
    }, [listenCount]);

    useEffect(() => {
        let tmpList = [];
        for (let i=0; i < listenData.length; i++){
            tmpList.push(
                <option key={`listen-${listenData[i]?.trx_id}`} value={[listenData[i]?.trx_id, listenData[i]?.pType]}>
                    {listenData[i]?.name} ({listenData[i]?.trx_id})
                </option>
            );
        }
        setListenList(tmpList);
        if (listenDevice === "" && listenData.length > 0){
            setListenDevice(() => listenData[0]?.trx_id);
            setListenPCode(() => listenData[0]?.pType);
            toast.success("Devices found.");
        }
    }, [listenData]);

    useEffect(() => {
        if (weekData){
            setLineColors(["#0081C7"]);
            setLineLabels(["Total Alarms"]);
            setLineData([weekData?.events?.length > 0 ? weekData.events : []]);
            setLineAxisLabels(weekData?.labels?.length > 0 ? weekData.labels : []);
        }
    }, [weekData]);

    function checkListenValid(){
        return [listening, deviceCatId !== 0, deviceTypeId !== 0].every((item) => item === true);
    }

    function basicActionHandling(){
        document.getElementById("name-static").classList.toggle("d-none");
        document.getElementById("name-edit").classList.toggle("d-none");
        document.getElementById("area-type-static").classList.toggle("d-none");
        document.getElementById("area-type-edit").classList.toggle("d-none");
        document.getElementById("occupancy-static").classList.toggle("d-none");
        document.getElementById("occupancy-edit").classList.toggle("d-none");
        document.getElementById("building-static").classList.toggle("d-none");
        document.getElementById("building-edit").classList.toggle("d-none");
        if (document.getElementById("edit-basic-btn").innerText === "Edit"){
            document.getElementById("edit-basic-btn").innerText = "Cancel";
            document.getElementById("save-basic-btn").classList.toggle("d-none");
            document.getElementById("name-edit").value = areaName;
            document.getElementById("area-type-edit").value = areaTypeId;
            document.getElementById("occupancy-edit").value = maxOccupancy;
            document.getElementById("building-edit").value = buildingId;
        }else{
            document.getElementById("edit-basic-btn").innerText = "Edit";
            document.getElementById("save-basic-btn").classList.toggle("d-none");
        }
        formCheck("basic-form", "save-basic-btn");
    }

    function basicSubmit(){
        let name, typeId, occupancy, payload, oldPayload, buildingId;
        if (editRights){
            name = document.getElementById("name-edit").value;
            typeId = document.getElementById("area-type-edit").value;
            occupancy = document.getElementById("occupancy-edit").value;
            buildingId = document.getElementById("building-edit").value;
            if (arrayComparison([name, parseInt(typeId), parseInt(occupancy), parseInt(buildingId)], [areaName, areaTypeId, maxOccupancy, buildingId])){
                handleSubmitActions("save-basic-btn", "load-basic-btn");
                payload = [{area_id: parseInt(areaId), area_name: name, area_type_id: typeId, max_occupancy: occupancy,
                            modified_by: dData.user, campus_id: campusId, building_id: buildingId,
                            vendor_ids: getActiveVendorIdList(),
                            }];
                oldPayload = [{area_id: parseInt(areaId), area_name: areaName, area_type_id: areaTypeId,
                               max_occupancy: maxOccupancy}];
                ApiRequester({reqEndpoint: "mytransaction", TransType: "area_edit_profile", Payload: payload,
                                    CampusId: campusId, User: dData.user, OldPayload: oldPayload,
                                    ClientId: dData.sessionId}).then(data => {
                    if (data && data[0]){
                        setCount(0);
                        formSuccess("Basic information saved.", "save-basic-btn", "load-basic-btn", "edit-basic-btn");
                    }
                    else{
                        formFail(`Error during save, error code: ${data && data?.length > 1 ? data[1] : ""}`, "save-basic-btn", "load-basic-btn");
                    }
                });
            }else{
                toast.info("No change")
            }
        }else{
            toast.warn("You do not have permissions to perform this action.");
        }
    }

    function addNoteModal(rowIndex){
        let dataIndex;
        dataIndex = parseInt(rowIndex);
        setStoredIndex(dataIndex);
        document.getElementById("note").value = "";
        setNoteTableLength(eventData[dataIndex].notes.length);
        setNoteTableRows(<TableBody start={0} end={eventData[dataIndex].notes.length}
                                    tableData={eventData[dataIndex].notes} checkbox={false}
                                     writeAccess={false} unk={"n-"} actionAppearance={() => {}}
                                     tableHeaders={noteHeaderData} modal={false}
                                     parentComponent={"Event"} dData={dData} checkboxlist={[]}
                                     setCheckboxId={() => {}} checkidkey={"note_id"}
                                     tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)}/>);
        formCheck("addNoteForm", "addNoteSubmit");
        showModal("addnotemodal");
    }

    function addNoteSubmit(){
        let payload, eventId, note;
        handleSubmitActions("addNoteSubmit", "loadAddNoteSubmit");
        eventId = eventData[storedIndex]?.event_id;
        note = document.getElementById("note").value;
        if (note === "0"){
            note = document.getElementById("note-freeform").value;
        }
        payload = [{event_id: eventId, campus_id: dData.acnt, note: note, modified_by: dData?.user}];
        ApiRequester({reqEndpoint: "mysave", Tbl: "core_event_notes", Payload: payload, urlType: "PUT",
                            User: dData.user, PKey: "note_id", Condition: "primary", CampusId: campusId,
                            ClientId: dData.sessionId}).then(data => {
            if (data[0] === true){
                formSuccess(`Note added successfully.`, "addNoteSubmit", "loadAddNoteSubmit", "cancelAddNoteBtn", "addNoteForm");
                setRepCount(0);
                document.getElementById("submitBtn").click()
            }
            else{
                formFail(`Error during add, error code: ${data[1]}`, "addNoteSubmit", "loadAddNoteSubmit");
            }
        });
    }

    function removeDevice(deviceId){
        let payload, oldPayload;
        if (devEditRights){
            handleSubmitActions("deviceConfirmSubmit", "loadDeviceConfirmSubmit");
            payload = [{device_id: deviceId, is_active: false, area_id: null, modified_by: dData.user,
                        vendor_ids: getActiveVendorIdList(),
                        }];
            oldPayload = [{device_id: deviceId, area_id: areaId}];
            ApiRequester({reqEndpoint: "mytransaction", TransType: "device_unassign_area", Payload: payload,
                                CampusId: campusId, User: dData.user, OldPayload: oldPayload,
                                ClientId: dData.sessionId}).then(data => {
                if (data && data[0] === true){
                    setCount(0);
                    formSuccess("Device has been removed", "deviceConfirmSubmit", "loadDeviceConfirmSubmit", "deviceCancelBtn");
                }
                else{
                    formFail("Failed to remove device", "deviceConfirmSubmit", "loadDeviceConfirmSubmit");
                }
            });
        }else{
            toast.warn("You do not have permissions to perform this action.");
        }
    }

    function deviceExists(serialNum){
        for (let i=0; i < deviceData?.length; i++){
            if (serialNum.toString() === deviceData[i]?.manufacture_uuid.toString()) {
                return deviceData[i];
            }
        }
        return false;
    }

    function getModelId(){
        let models = [], ret = 0;
        for (let i=0; i < deviceTypeData.length; i++){
            if (deviceTypeData[i]?.inovonics_type_id === deviceTypeId){
                models = deviceTypeData[i]?.models ? deviceTypeData[i].models : [];
                break;
            }
        }
        for (let i2=0; i2 < models.length; i2++){
            if (listenPCode === models[i2].product_type_id){
                ret = models[i2].model_id;
            }
        }
        return ret;
    }

    function pendantSubmit(initial){
        let description, msg, payload, serialNum, dRec, nid, mId, oldPayload;
        if (devEditRights){
            if (showDeviceList){
                serialNum = listenDevice;
            }else{
                if (!serialNum || serialNum === ""){
                    serialNum = document.getElementById("serialNum").value;
                }
                if (serialNum && !isNaN(serialNum)){
                    serialNum = parseInt(serialNum);
                }
            }
            dRec = deviceExists(serialNum);
            if (initial){
                if (dRec && dRec?.is_active){
                    setAssignedArea(dRec?.area_name);
                    hideModal("pendantmodal");
                    showModal("pendantconfirmmodal");
                    return;
                }else{
                    handleSubmitActions("pendantSubmit", "loadPendantSubmit");
                }
            }else{
                handleSubmitActions("pendantConfirmSubmit", "loadPendantConfirmSubmit");
            }

            if (listening){
                setListening(false);
                setListenCount(60);
            }

            description = document.getElementById("description").value;
            mId = modelId;
            if (!iAmHigherAuthority(80, dData.role_weight) && showDeviceList){
                mId = getModelId(listenPCode);
            }

            if (dRec){
                // Edit device and add to area
                payload = [{device_id: dRec.device_id, campus_id: campusId, is_active: true, area_id: parseInt(areaId),
                            device_use_id: parseInt(useId), device_name: deviceTypeName, manufacture_model_id: parseInt(mId),
                            descriptions: description, is_area_device: true, is_resident: false, resident_id: 0,
                            device_type_id: deviceTypeId, modified_by: dData.user,
                            vendor_ids: getActiveVendorIdList(),
                            }];
                oldPayload = [{device_id: dRec.device_id, campus_id: dRec?.campus_id, is_active: dRec?.is_active,
                               area_id: dRec?.area_id, device_use_id: dRec?.device_use_id,
                               device_name: dRec?.device_name, descriptions: dRec?.descriptions,
                               manufacture_model_id: dRec?.manufacture_model_id, device_type_id: dRec?.device_type_id}];
                if (["135", "131"].includes(useId.toString())){
                    if (["135"].includes(useId.toString())){
                        nid = document.getElementById("nid")?.value;
                        if (!nid){
                            nid = 0;
                        }
                    }else{
                        nid = -1;
                    }
                    payload[0]["network_id"] = parseInt(nid);
                    ApiRequester({reqEndpoint: "mytransaction", TransType: "system_device_edit", Payload: payload,
                                CampusId: campusId, User: dData.user, OldPayload: oldPayload,
                                ClientId: dData.sessionId}).then(data => {
                        if (data[0]) {
                            msg = `${deviceTypeName} has been added to ${areaName}`;
                            if (!initial){
                                handleSubmitActions("loadPendantConfirmSubmit", "pendantConfirmSubmit");
                                handleSubmitActions("loadPendantSubmit", "pendantSubmit");
                                swapModal("pendantConfirmClose", "editmodal");
                                document.getElementById("pendantForm").reset();
                                toast.success(msg);
                                setCount(0);
                                setListenEnabled(false);
                            }else{
                                formSuccess(msg, "pendantSubmit", "loadPendantSubmit",
                                "cancelPendantBtn", "pendantForm");
                                setCount(0);
                            }
                            setShowDeviceList(false);
                        } else {
                            toast.error(`Failed to add ${deviceTypeName}`);
                        }
                    });
                }else{
                    ApiRequester({reqEndpoint: "mytransaction", TransType: "device_assign_area", Payload: payload,
                                CampusId: campusId, User: dData.user, OldPayload: oldPayload,
                                ClientId: dData.sessionId}).then(data => {
                        if (data && data[0] === true){
                            msg = `${deviceTypeName} has been added to ${areaName}`;
                            if (!initial){
                                handleSubmitActions("loadPendantConfirmSubmit", "pendantConfirmSubmit");
                                hideModal("pendantconfirmmodal");
                                document.getElementById("pendantForm").reset();
                                toast.success(msg);
                                setCount(0);
                                setListenEnabled(false);
                            }else{
                                formSuccess(msg, "pendantSubmit", "loadPendantSubmit", "cancelPendantBtn", "pendantForm");
                                setCount(0);
                            }
                            setShowDeviceList(false);
                        }
                        else{
                            toast.error(`Failed to add ${deviceTypeName}`);
                        }
                    });
                }
            }else{
                // Add device and add to area
                payload = [{campus_id: campusId, manufacture_id: 1, manufacture_uuid: serialNum.toString(), device_name: deviceTypeName,
                            device_use_id: parseInt(useId), is_active: true, is_alerting_now: false, is_server: false,
                            is_area_device: true, is_resident: false, area_id: parseInt(areaId), manufacture_model_id: parseInt(mId),
                            escalation_enabled: true, descriptions: description, modified_by: dData.user, device_type_id: deviceTypeId,
                            vendor_ids: getActiveVendorIdList(),
                            }];
                if (["135", "131"].includes(useId.toString())){
                    if (["135"].includes(useId.toString())){
                        nid = document.getElementById("nid")?.value;
                        if (!nid){
                            nid = 0;
                        }
                    }else{
                        nid = -1;
                    }
                    payload[0]["network_id"] = parseInt(nid);
                    ApiRequester({reqEndpoint: "mytransaction", TransType: "system_device_add", Payload: payload,
                                CampusId: campusId, User: dData.user, ClientId: dData.sessionId}).then(data => {
                        if (data[0]) {
                            formSuccess(`${deviceTypeName} has been added to ${areaName}`, "pendantSubmit", "loadPendantSubmit",
                                "cancelPendantBtn", "pendantForm");
                            setCount(0);
                            setShowDeviceList(false);
                        } else {
                            toast.error(`Failed to add ${deviceTypeName}`);
                        }
                    });
                }else{
                    ApiRequester({reqEndpoint: "mytransaction", TransType: "device_add_area", Payload: payload,
                                CampusId: campusId, User: dData.user, OldPayload: [],
                                ClientId: dData.sessionId}).then(data => {
                        if (data && data[0] === true){
                            formSuccess(`${deviceTypeName} has been added to ${areaName}`, "pendantSubmit", "loadPendantSubmit",
                                 "cancelPendantBtn", "pendantForm");
                            setCount(0);
                            setShowDeviceList(false);
                        }
                        else{
                            toast.error(`Failed to add ${deviceTypeName}`);
                        }
                    });
                }
            }
        }else{
            toast.warn("You do not have permissions to perform this action.");
        }
    }

    function publishListen(){
        let sendPtype, modelStuff;
        if (dData.serverVersion === "" || versionSort(dData.serverVersion, "4.04.14") >= 0){
            sendPtype = iAmHigherAuthority(80, dData.role_weight) ? [productCode] : allProductCodes;
            modelStuff = iAmHigherAuthority(80, dData.role_weight) ? `Specific ${productCode}` : "All codes";
        }else{
            sendPtype = productCode;
            modelStuff = productCode;
        }
        ApiRequester({reqEndpoint: "mqtt", Operation: "learnin", ClientId: dData.sessionId,
                            ProductType: sendPtype, CampusId: dData?.acnt, MsgClass: msgClass}).then(data => {
            if (data[0]){
                toast.info(`Listening for ${deviceTypeName} devices, Model Code: ${modelStuff}`);
                setListenCount(0);
            }
            else{
                toast.error("Error with auto listening, try again, " +
                    "if persist contact your customer success for assistance.");
                setListening(false);
                setShowDeviceList(false);
            }
        });
    }

    const eventTableHeaders = <TableHeaders checkbox={false} headerData={eventHeaderData} checkAll={() => {}}
                                            writeAccess={true} sortOrder={""}
                                            checkboxHeaderId={"uCheckHead"} sortColumn={""} sorter={{}}
                                            setSorter={() => {}} modal={dData?.event_notes_active} modal_name={"Notes"} checkboxlist={[]}
                                            actionAppearance={() => {}}/>
    const notesTableHeaders = <TableHeaders checkbox={false} headerData={noteHeaderData}
                                            checkAll={() => {}} writeAccess={editRights} sortOrder={"asc"}
                                            sortColumn={"create_time"} sorter={{}}
                                            setSorter={() => {}} modal={false} checkboxlist={[]}
                                            actionAppearance={() => {}}/>
    const logTableHeaders = <TableHeaders checkbox={false} headerData={logHeaderData} checkAll={() => {}}
                                          writeAccess={false} sortOrder={logSorter.sortOrder} modal_name={""}
                                          originData={logOriginData} headerFiltersOn={true}
                                          sortColumn={logSorter.sortColumn} sorter={logSorter} setSorter={setLogSorter}
                                          modal={false} checkboxlist={[]} actionAppearance={() => {}}
                                          headerFilters={logHeaderFilters} setHeaderFilters={setLogHeaderFilters} setPage={setLogPage}/>

    const headerContent = (
        <div className={"d-flex justify-content-between"}>
        </div>
    );

    return (
        <>
            <Navigation />
            <div className={"main-content"}>
                <Header preTitle={"Area Profile"} Title={areaName} content={headerContent}/>
                <div className={"container-fluid"}>
                    <div className={"col"}>
                        <ul className={"nav nav-tabs nav-overflow header-tabs"}>
                            <li key={"set-basic"} className={"nav-item"}>
                                <Button text={"Basic Info"} spinner={!basicLoaded}
                                        class={tabItem === "basic" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("basic")}/>
                            </li>
                            <li key={"set-reporting"} className={"nav-item"}>
                                <Button text={"Reporting"} spinner={!reportLoaded}
                                        class={tabItem === "reporting" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("reporting")}/>
                            </li>
                            <li key={"set-devices"} className={"nav-item"}>
                                <Button text={"Devices"} spinner={!devicesLoaded}
                                        class={tabItem === "devices" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("devices")}/>
                            </li>
                            <li key={"set-wellness"} className={"nav-item"}>
                                <Button text={"Wellness"} spinner={!wellnessLoaded}
                                        class={tabItem === "wellness" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("wellness")}/>
                            </li>
                            {iAmHigherAuthority(80, dData?.role_weight)
                                ?   <li key={"set-logging"} className={"nav-item"}>
                                        <Button text={"Logs"} spinner={!logsLoaded}
                                                class={tabItem === "logs" ? "nav-link active" : "nav-link"}
                                                onClick={() => setTabItem("logs")}/>
                                    </li>
                                :   <></>
                            }
                        </ul>
                    </div>
                    <div className={tabItem === "init" ? "d-block" : "d-none"}>
                        <ProfileSkeleton />
                    </div>
                    <div className={tabItem === "basic" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "550px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                <h3 className={"card-header-title d-inline-block"}>Basic Information</h3>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"basic-card-btn"}
                                       onClick={() => toggleCollapse("basic-card-btn", "basic-card")}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"basic-card"}>
                                <form className={"d-inline align-top form-control-plaintext was-validated"}
                                      id={"basic-form"}>
                                    <div className={"mx-5 mt-2 align-top d-inline-block"} id={"basic-col1-div"}>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "75px"}}>Name:</p>
                                            <p className={"ms-4"} id={"name-static"}>{areaName}</p>
                                            <input className={"ms-4 d-none form-control form-control-sm"}
                                                   required={true}
                                                   style={{height: "30px", width: "125px"}} id={"name-edit"}
                                                   defaultValue={areaName} pattern={"^[0-9a-zA-Z_\\- ]+$"}/>
                                        </div>
                                        <div className={"mt-2 d-flex"}>
                                            <p style={{minWidth: "75px"}}>Type:</p>
                                            <p className={"ms-4"} id={"area-type-static"}>{areaType}</p>
                                            <select className={"ms-4 d-none form-select form-select-sm"}
                                                    onChange={() => formCheck("basic-form", "save-basic-btn")}
                                                    style={{height: "30px", width: "256px"}} id={"area-type-edit"}
                                                    defaultValue={areaTypeId}>
                                                {areaTypeOptions}
                                            </select>
                                        </div>
                                        <div className={"mt-2 d-flex"}>
                                            <p style={{minWidth: "75px"}}>Building:</p>
                                            <p className={"ms-4"} id={"building-static"}>{building}</p>
                                            <select className={"ms-4 d-none form-select form-select-sm"}
                                                    onChange={() => formCheck("basic-form", "save-basic-btn")}
                                                    style={{height: "30px", width: "256px"}} id={"building-edit"}
                                                    defaultValue={buildingId}>
                                                <option value={"0"} disabled={buildingOptions.length > 0}>Main (Campus Default)</option>
                                                {buildingOptions}
                                            </select>
                                        </div>
                                        <div className={"mt-2 d-flex"}>
                                            <p style={{minWidth: "75px"}}>Capacity:</p>
                                            <p className={"ms-4"} id={"occupancy-static"}>{maxOccupancy}</p>
                                            <input className={"ms-4 d-none form-control form-control-sm"}
                                                   type={"number"}
                                                   style={{height: "30px", width: "256px"}} id={"occupancy-edit"}
                                                   defaultValue={maxOccupancy} step={1} max={100} min={0}
                                                   onChange={() => formCheck("basic-form", "save-basic-btn")}/>
                                        </div>
                                        <div className={"mt-2 d-flex"}>
                                            <ul className={"ps-0 d-inline-block align-top mb-0"}
                                                style={{listStyle: "none"}}>
                                                <li><p style={{minWidth: "75px"}} className={"me-4"}>Residents:</p></li>
                                            </ul>
                                            <ul className={"ps-0 d-inline-block mb-0"} style={{listStyle: "none"}}>
                                                <li>{residents}</li>
                                            </ul>
                                        </div>
                                        <div className={"mt-2 d-flex"}>
                                            <ul className={"ps-0 d-inline-block align-top mb-0"}
                                                style={{listStyle: "none"}}>
                                                <li><p style={{minWidth: "75px"}} className={"me-4"}>Groups:</p></li>
                                            </ul>
                                            <ul className={"ps-0 d-inline-block mb-0"} style={{listStyle: "none"}}>
                                                {areaGroups.map((group, i) => {
                                                    return <li
                                                        key={`area-group-name-item-${i}`}>{group?.area_group_name}</li>
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                </form>
                                {editRights
                                    ?   <>
                                            <Button text={"Edit"} class={"btn btn-primary float-end"} id={"edit-basic-btn"}
                                                    disabled={!editRights} onClick={() => {
                                                if (editRights) {
                                                    basicActionHandling();
                                                }
                                            }}/>
                                            <Button text={"Save"} class={"btn btn-primary float-end me-3 d-none"}
                                                    id={"save-basic-btn"}
                                                    disabled={!editRights} onClick={() => {
                                                if (editRights) {
                                                    basicSubmit();
                                                }
                                            }}/>
                                            <Button type={"button"} class={"btn btn-primary float-end mx-3 d-none"}
                                                    disabled={true}
                                                    spinner={true} text={""} id={"load-basic-btn"}/>
                                        </>
                                    :   <></>
                                }
                            </div>
                        </div>
                    </div>
                    <div className={tabItem === "devices" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "500px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"}
                                        style={{minWidth: "80px"}}>Devices</h3>
                                    <div className={"d-none"} style={{display: "inline-block"}}
                                         id={"devices-header-data"}>
                                        <h3 className={"ms-6 my-0 d-inline-block text-success"}>{devices.length} {devices?.length === 1 ? "Device" : "Devices"}</h3>
                                    </div>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"devices-card-btn"}
                                       onClick={() => {
                                           toggleCollapse("devices-card-btn", "devices-card");
                                           document.getElementById("devices-header-data").classList.toggle("d-none");
                                       }}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"devices-card"}>
                                <ul className={"d-inline-block"} style={{listStyle: "none"}}>
                                    {devices}
                                </ul>
                                {devEditRights
                                    ?   <Button text={"Attach"} class={"btn btn-primary float-end me-3"}
                                                id={"device-modal-btn"}
                                                disabled={!devEditRights}
                                                onClick={() => {
                                                    if (devEditRights) {
                                                        setDeviceCatId(0);
                                                        setDeviceTypeId(0);
                                                        setModelId(0);
                                                        setListening(false);
                                                        setShowDeviceList(false);
                                                        setListenDevice("");
                                                        setListenData([]);
                                                        setListenList([]);
                                                        formCheck("pendantForm", "pendantSubmit");
                                                        showModal("pendantmodal");
                                                    }
                                            }}/>
                                    :   <></>
                                }
                            </div>
                        </div>
                    </div>
                    <div className={tabItem === "reporting" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "500px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"} style={{minWidth: "80px"}}>
                                        Reporting
                                    </h3>
                                    <div className={"d-none"} style={{display: "inline-block"}}
                                         id={"reporting-header-data"}>
                                        <h3 className={"ms-6 my-0 d-inline-block text-success"}>{!isNaN(hourData?.avgValue) ? hourData.avgValue : "?"} Alert/Week</h3>
                                        <h3 className={"mx-4 my-0 d-inline-block text-success"}>{!isNaN(weekData?.avgValue) ? weekData.avgValue : "?"} Avg/Resp</h3>
                                    </div>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"reporting-card-btn"}
                                       onClick={() => {
                                           toggleCollapse("reporting-card-btn", "reporting-card")
                                           document.getElementById("reporting-header-data").classList.toggle("d-none");
                                       }}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"reporting-card"}>
                                <div className={"row"}>
                                    <Link style={{width: "auto", marginLeft: "auto"}} to={"/reports/alerts/run"} onClick={() => {
                                        setAreaFilters(dData, areaId, areaName);
                                    }}>
                                        <Button text={"Run Detailed Report"} class={"btn btn-primary mb-4 float-end"}
                                                icon={true} iconClass={"fe fe-arrow-right-circle float-end ms-3"}/>
                                    </Link>
                                </div>
                                <div className={"row"}>
                                    <div className={"chart-container w-50 text-end align-top d-inline-block"}>
                                        {hourDataLoaded
                                            ?
                                            <HourlyLineAndBarChart
                                                style={{maxHeight: "425px"}}
                                                labels={hourData?.labels ? hourData.labels : ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
                                                                "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23"]}
                                                x_axis={true}
                                                x_axis_title={"Time of the Day"}
                                                bar_axis={true}
                                                bar_axis_title={"Total Alarms"}
                                                bar_color={"#0081C7"}
                                                line_axis={true}
                                                line_axis_title={"Average Response Time"}
                                                line_color={"#BE4D25"}
                                                bar_data={hourData?.events ? hourData.events : []}
                                                line_data={hourData?.responses ? hourData.responses : []}
                                                bar_annotation={true}
                                                line_annotation={true}
                                                forceAspect={true}
                                                bar_annotation_value={hourData?.avgValue ? hourData.avgValue : 0}
                                                line_annotation_value={hourData?.avgResp ? hourData.avgResp : 0} />
                                            :
                                            (<div className={"text-center"}>
                                                <Button text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}></Button>
                                            </div>)
                                        }
                                    </div>
                                    <div className={"chart-container w-50 text-center align-top d-inline-block"}>
                                        {weekDataLoaded
                                            ?
                                            <LineTrendingChart
                                                style={{maxHeight: "425px"}}
                                                x_axis={true}
                                                x_axis_title={"Weeks"}
                                                line_axis={true}
                                                line_axis_title={"Total Alarms"}
                                                lines={1}
                                                line_color={lineColors}
                                                line_labels={lineLabels}
                                                line_data={lineData}
                                                forceAspect={true}
                                                labels={lineAxisLabels} />
                                            :
                                            (<div className={"text-center"}>
                                                <Button text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}></Button>
                                            </div>)
                                        }
                                    </div>
                                </div>
                                <div className={"row mt-5"}>
                                    <div className={"w-50 px-4"}>
                                        <table className={"table table-sm table-white table-hover"}>
                                            <thead>
                                            <tr>
                                                <th className={"table table-header w-auto py-1"} colSpan={"4"}>
                                                    Event Reason Breakdown
                                                </th>
                                            </tr>
                                            <tr>
                                                <th className={"table table-header w-auto py-1"}>
                                                    Reason
                                                </th>
                                                <th className={"table table-header w-auto py-1"}>
                                                    Total
                                                </th>
                                                <th className={"table table-header w-auto py-1"}>
                                                    % Total Volume
                                                </th>
                                                <th className={"table table-header w-auto py-1"}>
                                                    Average Response (min)
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td className={"ps-1 fw-bold"}>Total Events</td>
                                                <td className={"ps-1 fw-bold text-center"}>{reasonData?.count ? reasonData.count : 0}</td>
                                                <td className={"ps-1 text-right"}></td>
                                                <td className={"ps-1 fw-bold text-center"}>{reasonData?.avg_response_time ? reasonData.avg_response_time : 0.0}</td>
                                            </tr>
                                            {reasonRows}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={"w-50"} style={{maxHeight: "400px", overflowY: "auto"}}>
                                        <table className={"table table-sm table-white table-hover d-sm-table"}>
                                            <thead className={"sticky-table-head"} style={{top: 0, zIndex: 1}}>
                                            {eventTableHeaders}
                                            </thead>
                                            <tbody>
                                            {eventTableRows}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={tabItem === "wellness" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "500px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"}
                                        style={{minWidth: "80px"}}>Wellness</h3>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"wellness-card-btn"}
                                       onClick={() => toggleCollapse("wellness-card-btn", "wellness-card")}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"wellness-card"}>
                                <div className={"ms-4"}>
                                    <div className={"d-block"}>
                                        <div className={"d-inline-block align-top me-4"}
                                             style={{minWidth: "125px"}}>Careflow Plan:
                                        </div>
                                        <div className={"d-inline-block"}>{careflow ? careflow : "No Careflow"}</div>
                                    </div>
                                    <div className={"d-block mt-3"}>
                                        <div className={"d-inline-block align-top me-4"}
                                             style={{minWidth: "125px"}}>Rounds:
                                        </div>
                                        <div
                                            className={"d-inline-block"}>{rounds && rounds?.length > 0 ? rounds : "No Rounds Needed"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={tabItem === "logs" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "500px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"}
                                        style={{minWidth: "80px"}}>Area Logs</h3>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"wellness-card-btn"}
                                       onClick={() => toggleCollapse("logs-card-btn", "logs-card")}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"logs-card"}>
                                <table className={"table table-sm table-white table-hover"}>
                                    <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                    {logTableHeaders}
                                    </thead>
                                    <tbody id={"tBody"}>
                                    {logTableRows}
                                    </tbody>
                                </table>
                                <div className={"text-center"}>
                                    <Pagination>
                                        <PaginationItems setPage={setLogPage} page={logPage} beginDot={logPPRules.beginDot}
                                                         endDot={logPPRules.endDot} pageMin={logPPRules.pageMin}
                                                         pageMax={logPPRules.pageMax}
                                                         max={logPPRules.max}/>
                                    </Pagination>
                                    <Button class={"btn btn-secondary fs-5 ms-4"} text={perPage}
                                            onClick={() => showModal("filtermodal")}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"modal fade"} id={"pendantmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticDeviceBackdropLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticDeviceBackdropLabel"}>Add Device</h2>
                            <button type={"button"} className={"btn-close"} id={"pendantClose"}
                                    data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <form id={"pendantForm"} className={"was-validated"}>
                            <div className={"modal-body"}>
                                <div className={"form-floating mb-3 d-inline-block me-2"} style={{width: "68%"}}>
                                    <select className={"form-select"} value={undefined}
                                            required={true} id={"device_category"}
                                            onChange={(e) => {
                                                setDeviceCatId(parseInt(e.target.value));
                                                setModelId(0);
                                                setDeviceTypeId(0);
                                                formCheck("pendantForm", "pendantSubmit");
                                            }}>
                                        <option value={""}></option>
                                        {deviceCatOptions}
                                    </select>
                                    <label htmlFor={"device_category"}>Device Category</label>
                                </div>
                                <div className={"d-flex justify-content-start"}>
                                    <div className={"form-floating mb-3 d-inline-block me-2"} style={{width: "68%"}}>
                                        <select className={"form-select"} value={undefined}
                                                required={true} id={"device_type_id"}
                                                onChange={(e) => {
                                                    setDeviceTypeId(parseInt(e.target.value));
                                                    setDeviceTypeName(e.target[e.target.selectedIndex].innerText);
                                                    areaLearninBehaviorToggle(e.target.value);
                                                    formCheck("pendantForm", "pendantSubmit");
                                                }}>
                                            <option value={""}></option>
                                            {deviceTypeOptions}
                                        </select>
                                        <label htmlFor={"device_type_id"}>Device Type</label>
                                        <div className={deviceImg ? "select-img-div" : "select-img-div-nohover"}>
                                            {deviceImg
                                                ?   <img src={`/static/image/${deviceImg}`}
                                                        alt={""}
                                                        className={"mx-auto"}
                                                        style={{
                                                            width: "inherit",
                                                            height: "inherit",
                                                            padding: "0 10px 6px 0"
                                                    }}/>
                                                :   <></>
                                            }
                                        </div>
                                    </div>
                                    <div className={"form-floating mb-3 d-inline-block ms-auto"} style={{width: "30%"}}>
                                        <select className={"form-select"} id={"modelId"} value={modelId}
                                                onChange={(e) => {
                                                    setModelId(parseInt(e.target.value));
                                                    formCheck("pendantForm", "pendantSubmit");
                                                }}>
                                            {modelOptions}
                                        </select>
                                        <label htmlFor={"modelId"}>Model</label>
                                    </div>
                                </div>
                                <div className={"form-floating mb-3"}>
                                    <input type={"text"} className={"form-control"} id={"description"}
                                           pattern={"^[0-9a-zA-Z_\\- ]+$"}/>
                                    <label htmlFor={"description"}>Description (optional)</label>
                                </div>
                                <div className={"d-flex justify-content-start"}>
                                    <div className={`form-floating mb-3 w-75 ${showDeviceList && "d-none"}`}
                                         id={"serialToggle"}>
                                        <input type={"text"} className={"form-control"} id={"serialNum"}
                                               required={!showDeviceList}
                                               onKeyUp={(e) => formCheckWithElement("pendantForm", "pendantSubmit", e, areaDeviceData)}
                                               pattern={"^[0-9]+$"}/>
                                        <label htmlFor={"serialNum"}>Serial Number</label>
                                    </div>
                                    <div className={`form-floating mb-3 w-75 ${!showDeviceList && "d-none"}`}
                                         id={"serial2Toggle"}>
                                        <select className={"form-select"} id={"serialNum2"} required={showDeviceList}
                                                onClick={(e) => {
                                                    let tmp = e.target.value.split(",");
                                                    setListenDevice(tmp[0]);
                                                    setListenPCode(parseInt(tmp[1]));
                                                }}>
                                            {listenList}
                                        </select>
                                        <label htmlFor={"serialNum2"}>Serial Numbers</label>
                                    </div>
                                    <Button text={"Auto Listen"} id={"listen-btn-1"}
                                            class={`btn ${listenEnabled ? "btn-primary" : "btn-danger"} m-auto ${showDeviceList && "d-none"}`}
                                            disabled={!listenEnabled} onClick={() => {
                                        setListening(true);
                                        setShowDeviceList(true);
                                        publishListen()
                                    }}/>
                                    <Button text={"Stop Listen"} id={"listen-btn-2"}
                                            class={`btn ${listening ? "btn-success" : "btn-warning"} m-auto ${!showDeviceList && "d-none"}`}
                                            disabled={!listenEnabled} onClick={() => {
                                        setListening(false);
                                        setShowDeviceList(false);
                                    }}/>
                                </div>
                                <div id={"ncsettings"} className={"d-none"}>
                                    <div className={"form-floating mb-3"}>
                                        <input type={"number"} className={"form-control"} id={"nid"}
                                               required={false} min={0} max={31} step={1} onChange={() => {
                                            formCheck("pendantForm", "pendantSubmit");
                                        }}/>
                                        <label htmlFor={"serialNum"}>Network ID</label>
                                        <div className={"invalid-feedback"}>
                                            Please enter a valid network id for coordinator. Valid 0 - 31
                                        </div>
                                    </div>
                                </div>
                                <div className={`list-group-item ${!showAlarmBehaviors && 'd-none'}`}
                                     id={"behavior-heading-container"}>
                                    <h4 className={"mb-4 text-center"}>Alarm Behavior</h4>
                                    {useOptions}
                                </div>
                            </div>
                            <div className={"modal-footer"}>
                                <div>
                                    <button type={"button"} className={"btn btn-secondary"} id={"cancelPendantBtn"}
                                            data-bs-dismiss={"modal"}
                                            onClick={() => {
                                                formClear("pendantForm", "pendantSubmit")
                                            }}>Cancel
                                    </button>
                                    <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true}
                                            spinner={true} text={"Loading"} id={"loadPendantSubmit"}/>
                                    <button type={"button"} className={"btn btn-primary ms-3"} id={"pendantSubmit"}
                                            onClick={() => pendantSubmit(true)}>Submit
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className={"modal fade"} id={"pendantconfirmmodal"} data-bs-backdrop={"static"}
                 data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticPendantConfirmLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticPendantConfirmLabel"}>Confirm Add</h2>
                            <button type={"button"} className={"btn-close"} id={"pendantConfirmClose"}
                                    data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <div className={"modal-body"}>
                            <p>Device is already linked to {assignedArea} Do you wish to re-assign this device?</p>
                        </div>
                        <div className={"modal-footer"}>
                            <button type={"button"} className={"btn btn-secondary"} style={{width: "75px"}}
                                    id={"pendantConfirmCancelBtn"}
                                    onClick={() => swapModal("pendantConfirmClose", "pendantmodal")}>No
                            </button>
                            <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true} spinner={true}
                                    text={"Loading"} id={"loadPendantConfirmSubmit"}/>
                            <button type={"button"} className={"btn btn-primary ms-3"} id={"pendantConfirmSubmit"}
                                    onClick={() => pendantSubmit(false)}>YES
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"modal fade"} id={"deviceconfirmmodal"} data-bs-backdrop={"static"}
                 data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticDeviceConfirmLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticDeviceConfirmLabel"}>Confirm Add</h2>
                            <button type={"button"} className={"btn-close"} id={"deviceConfirmClose"}
                                    data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <div className={"modal-body"}>
                            <p>Are you sure you wish to permanently detach this device from {areaName}?</p>
                        </div>
                        <div className={"modal-footer"}>
                            <button type={"button"} className={"btn btn-secondary"} style={{width: "75px"}}
                                    id={"deviceCancelBtn"} data-bs-dismiss={"modal"}>No
                            </button>
                            <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true} spinner={true}
                                    text={"Loading"} id={"loadDeviceConfirmSubmit"}/>
                            <button type={"button"} className={"btn btn-primary ms-3"} id={"deviceConfirmSubmit"}
                                    onClick={() => removeDevice(deviceToRemove)}>YES
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"modal fade"} id={"addnotemodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticAddNoteBackdropLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title text-capitalize"} id={"staticAddNoteBackdropLabel"}>Add
                                Note</h2>
                            <button type={"button"} className={"btn-close"} id={"addNoteClose"}
                                    data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <form id={"addNoteForm"} className={"was-validated"}>
                            <div className={"modal-body"}>
                                {dData?.event_notes_preform
                                    ? <div className={"form-floating d-block"}>
                                        <select className={"form-select form-select my-2"} id={"note"}
                                                required={true}
                                                onChange={(e) => {
                                                    if (e.target.value === "0") {
                                                        document.getElementById("freeform").classList.remove("d-none");
                                                        document.getElementById("note-freeform").required = true;
                                                    } else {
                                                        document.getElementById("note-freeform").required = false;
                                                        document.getElementById("freeform").classList.add("d-none");
                                                    }
                                                    formCheck("addNoteForm", "addNoteSubmit");
                                                }}>
                                            {prenoteOptions}
                                        </select>
                                        <label htmlFor={"note"} className={"form-label"}>Note</label>
                                    </div>
                                    :   <div className={"my-2 form-floating"}>
                                            <textarea className={"form-control pt-5"} id={"note"} required={true}
                                                      style={{height: "125px"}}
                                                      onKeyUp={() => formCheck("addNoteForm", "addNoteSubmit")}>

                                            </textarea>
                                            <label htmlFor={"note"} className={"form-label"}>Note</label>
                                            <div className={"invalid-feedback"}>
                                                Note required
                                            </div>
                                        </div>
                                }
                                <div className={"my-2 form-floating d-none"} id={"freeform"}>
                                    <textarea className={"form-control pt-5"} id={"note-freeform"}
                                              style={{height: "125px"}}
                                              onKeyUp={() => formCheck("addNoteForm", "addNoteSubmit")}>
                                    </textarea>
                                    <label htmlFor={"note-freeform"} className={"form-label"}>Note</label>
                                    <div className={"invalid-feedback"}>
                                        Note required
                                    </div>
                                </div>
                                {noteTableLength > 0
                                    ? <>
                                        <hr/>
                                        <table className={"table table-sm table-white table-hover"}>
                                            <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                            {notesTableHeaders}
                                            </thead>
                                            <tbody id={"tBody"}>
                                            {noteTableRows}
                                            </tbody>
                                        </table>
                                    </>
                                    : <></>
                                }
                            </div>
                            <div className={"modal-footer"}>
                                <div>
                                    <button type={"button"} className={"btn btn-secondary"} id={"cancelAddNoteBtn"}
                                            data-bs-dismiss={"modal"}>Cancel
                                    </button>
                                    <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true}
                                            spinner={true} text={"Loading"} id={"loadAddNoteSubmit"}/>
                                    <button type={"button"} className={"btn btn-primary ms-3"} id={"addNoteSubmit"}
                                            onClick={() => addNoteSubmit()}>Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Suspense fallback={"Loading..."}>
                <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                autoClose={10000} closeOnClick={true}/>
            </Suspense>
            <button
                type={"button"}
                className={"help-corner-button"}
                onClick={() => showModal("helpmodal")}
                title={"Page Help Popup"}
                aria-label={"Open Help"}
            >
                <i className={"fe fe-help-circle help-corner-button-icon"}></i>
            </button>
            <div className={"modal fade help-modal-pop"} id={"helpmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticHelpBackdropLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog modal-lg"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticHelpBackdropLabel"}>Area Profile Page Help</h2>
                            <button type={"button"} className={"btn-close"} data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <div className={"modal-body"}>
                            <div className={"mb-4"}>
                                <h4 className={"text-primary mb-3"}>
                                    <i className={"fe fe-info me-2"}></i>Getting Started
                                </h4>
                                <p className={"mb-2"}>
                                    This page provides detailed information about a specific area in your community, including basic information, reporting data, device assignments, wellness settings, and activity logs.
                                </p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-layers me-2 text-primary"}></i>Page Tabs
                                </h5>
                                <p className={"mb-2"}>The page is organized into several tabs:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Basic Info</strong>: View and edit area details such as name, type, building, capacity, residents, and groups</li>
                                    <li className={"mb-2"}><strong>Reporting</strong>: View charts and statistics about alarms, response times, and event reasons for this area</li>
                                    <li className={"mb-2"}><strong>Devices</strong>: View and manage devices assigned to this area</li>
                                    <li className={"mb-2"}><strong>Wellness</strong>: View careflow plans and rounds schedules for this area</li>
                                    {iAmHigherAuthority(80, dData.role_weight) ? <li className={"mb-2"}><strong>Logs</strong>: View activity logs for this area (available to higher authority users)</li> : <></>}
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-info me-2 text-primary"}></i>Basic Info Tab
                                </h5>
                                <p className={"mb-2"}>This tab displays the area's basic information:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Name</strong>: The area's name (editable with proper permissions)</li>
                                    <li className={"mb-2"}><strong>Type</strong>: The area type classification (editable)</li>
                                    <li className={"mb-2"}><strong>Building</strong>: The building this area belongs to (editable)</li>
                                    <li className={"mb-2"}><strong>Capacity</strong>: Maximum occupancy for the area (editable)</li>
                                    <li className={"mb-2"}><strong>Residents</strong>: List of residents currently assigned to this area (clickable links to resident profiles)</li>
                                    <li className={"mb-2"}><strong>Groups</strong>: Area groups this area belongs to</li>
                                </ul>
                                <p className={"mb-2"}>Click the "Edit" button to modify area information. Changes require appropriate permissions.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-bar-chart-2 me-2 text-primary"}></i>Reporting Tab
                                </h5>
                                <p className={"mb-2"}>This tab provides visual analytics and event data:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Hourly Chart</strong>: Shows alarm frequency by hour of day and average response times</li>
                                    <li className={"mb-2"}><strong>Weekly Trend Chart</strong>: Displays alarm trends over time</li>
                                    <li className={"mb-2"}><strong>Event Reason Breakdown</strong>: Table showing alarm reasons, counts, percentages, and average response times</li>
                                    <li className={"mb-2"}><strong>Recent Events Table</strong>: List of recent alarms with details including event time, response time, device, resident, and reason</li>
                                </ul>
                                <p className={"mb-2"}>Click "Run Detailed Report" to generate a comprehensive report for this area.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-smartphone me-2 text-primary"}></i>Devices Tab
                                </h5>
                                <p className={"mb-2"}>This tab shows all devices assigned to the area:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>View all devices linked to this area</li>
                                    <li className={"mb-2"}>Click on device names to view device profiles</li>
                                    <li className={"mb-2"}>Use the "Attach" button to add new devices to the area</li>
                                    <li className={"mb-2"}>Remove devices using the trash icon (requires appropriate permissions)</li>
                                </ul>
                                <p className={"mb-2"}>When adding devices, you can use "Auto Listen" for Inovonics devices to automatically detect serial numbers upon devie activation.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-heart me-2 text-primary"}></i>Wellness Tab
                                </h5>
                                <p className={"mb-2"}>This tab displays wellness-related information:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Careflow Plan</strong>: Shows careflow windows that apply to this area based on area groups</li>
                                    <li className={"mb-2"}><strong>Rounds</strong>: Displays scheduled rounds windows for this area</li>
                                </ul>
                                <p className={"mb-2"}>These settings are configured at the system level and displayed here for reference.</p>
                            </div>

                            {iAmHigherAuthority(80, dData.role_weight)
                                ? <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-file-text me-2 text-primary"}></i>Logs Tab
                                    </h5>
                                    <p className={"mb-2"}>This tab (available to higher authority users) shows activity logs:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>View all changes and activities related to this area</li>
                                        <li className={"mb-2"}>Filter logs by event type and modified by</li>
                                        <li className={"mb-2"}>Sort and paginate through log entries</li>
                                    </ul>
                                </div>
                                : <></>
                            }

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-edit me-2 text-primary"}></i>Adding Notes to Events
                                </h5>
                                <p className={"mb-2"}>In the Reporting tab, you can add notes to events:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Click the notes badge on any event row</li>
                                    <li className={"mb-2"}>Select a preformatted note or enter a custom note</li>
                                    <li className={"mb-2"}>View existing notes in the modal</li>
                                    <li className={"mb-2"}>Notes are timestamped and show who created them</li>
                                </ul>
                            </div>

                            <div className={"mb-3"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-settings me-2 text-primary"}></i>Additional Tips
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Use the collapse/expand icons on card headers to show or hide sections</li>
                                    <li className={"mb-2"}>Charts automatically refresh every 20 minutes with the latest data</li>
                                    <li className={"mb-2"}>Basic information automatically refreshes every 10 minutes</li>
                                    <li className={"mb-2"}>Click on resident names to navigate to their profiles</li>
                                    <li className={"mb-2"}>Click on device names to view device details</li>
                                    <li className={"mb-2"}>Event reason indicators show if a reason has been edited</li>
                                    <li className={"mb-2"}>Use pagination controls at the bottom of tables to navigate through data</li>
                                </ul>
                            </div>
                        </div>
                        <div className={"modal-footer"}>
                            <button type={"button"} className={"btn btn-secondary"} data-bs-dismiss={"modal"}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AreaProfile;